/**
 * /online-camera — the page: a camera with a real shutter, self-timer, burst,
 * photo booth, time-lapse, night stacking, manual controls where the camera
 * gives them, composition aids, hands-free shutters and a roll that survives a
 * reload.
 *
 * The pieces live next door: core.js (all the arithmetic, node-tested),
 * camera.js (open / still / constraints through CameraManager), develop.js
 * (crop · look · imprint · encode), store.js (IndexedDB roll), booth.js
 * (print sheet), smart.js (smile / palm / clap), timelapse.js (interval clock,
 * exporters, night stack). This file wires them to the controls.
 */
import * as C from './core.js';
import { Camera, withTimeout } from './camera.js';
import { develop, encode, thumbnail, ctxFilterWorks, webpEncodes } from './develop.js';
import { Roll } from './store.js';
import { composeBooth, printAtSize, printInches } from './booth.js';
import { SmartShutter } from './smart.js';
import { workerTicker, Lapse, exportMp4, exportGif, stackFrames } from './timelapse.js';
import { RemoteHost, RemoteGuest, validCode } from './remote.js';
import { withExif } from './exif.js';

const CFG = window.__OC_CFG || {};
const SLUG = CFG.slug || 'online-camera';
const LANG = CFG.lang || {};
const $ = (id) => document.getElementById(id);

/** Laravel-style «:name» placeholders; longest names first so :s never eats :size. */
function T(key, vars) {
    let s = LANG[key] != null ? String(LANG[key]) : key;
    if (vars) {
        Object.keys(vars).sort((a, b) => b.length - a.length).forEach((k) => { s = s.split(':' + k).join(String(vars[k])); });
    }
    return s;
}
const toast = (m, type) => { try { if (window.__toast) window.__toast(m, type || 'info'); } catch (_) { } };
const feat = (f, p) => { try { if (window.__aeFeature) window.__aeFeature(f, p || {}); } catch (_) { } };
const stage = (s, p) => { try { if (window.__stage) window.__stage(s, p || {}); } catch (_) { } };
const diag = (p) => { try { if (window.__aeDiagnostic) window.__aeDiagnostic(p); } catch (_) { } };
const sleep = (ms, sig) => new Promise((resolve, reject) => {
    const t = setTimeout(resolve, ms);
    if (sig) sig.addEventListener('abort', () => { clearTimeout(t); reject(new DOMException('aborted', 'AbortError')); }, { once: true });
});
const isMobile = /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || '') || (navigator.maxTouchPoints > 1 && /Macintosh/.test(navigator.userAgent || ''));

/* ───────────────────────── settings (remembered per browser) ───────────────────────── */
const TS = window.__toolSettings;
const DEF = {
    mode: 'photo', aspect: 'full', timer: 0, format: 'jpeg', quality: 92, sound: true, blink: true, screenLight: false, date: false,
    look: 'none', res: 'max', device: '', mirrorFront: true, mirrorSave: false, sensor: true, flashMode: 'off',
    grid: 'none', level: false, hist: false, zebra: false, zebraThr: 95, peak: false, peakSens: 50, peakColor: 'red',
    smart: 'off', smileThr: 55, smileAll: false, clapSens: 50,
    burstN: 5, burstIv: 3, boothLayout: 'strip', boothOut: 'strip', boothN: 4, boothCd: 3, boothCap: '', boothDate: true,
    boothTheme: 'white', boothKeep: false, tlIv: 5, tlFps: 24, tlMax: 0, nightN: 16, nightBoost: 3,
};
const S = {};
for (const k of Object.keys(DEF)) {
    let v = DEF[k];
    try { if (TS) v = TS.load(SLUG, k, DEF[k]); } catch (_) { }
    if (typeof DEF[k] === 'number') v = Number(v); else if (typeof DEF[k] === 'boolean') v = v === true || v === 'true';
    S[k] = (typeof DEF[k] === 'number' && !isFinite(v)) ? DEF[k] : v;
}
// a hands-free microphone shutter is never switched on by a page load
if (S.smart === 'clap') S.smart = 'off';
function save(k, v) { S[k] = v; try { if (TS) TS.save(SLUG, k, v); } catch (_) { } }

/* ───────────────────────── elements ───────────────────────── */
const el = {
    app: $('ocApp'), vfwrap: $('ocVfWrap'), vf: $('ocVf'), video: $('ocVideo'), wash: $('ocWash'), vignette: $('ocVignette'), grain: $('ocGrain'),
    assist: $('ocAssist'), guide: $('ocGuide'), histo: $('ocHisto'), clip: $('ocClip'), badge: $('ocBadge'), focus: $('ocFocus'),
    count: $('ocCount'), flash: $('ocFlash'), idle: $('ocIdle'), start: $('ocStart'), fail: $('ocFail'), failText: $('ocFailText'), retry: $('ocRetry'),
    kiosk: $('ocKiosk'), kioskGo: $('ocKioskGo'), kioskExit: $('ocKioskExit'), kioskRes: $('ocKioskRes'), kioskImg: $('ocKioskImg'), kioskMsg: $('ocKioskMsg'),
    flip: $('ocFlip'), modes: $('ocModes'), shutter: $('ocShutter'), last: $('ocLast'), fs: $('ocFs'), info: $('ocInfo'),
    result: $('ocResult'), resultH: $('ocResultH'), resultImg: $('ocResultImg'), resBooth: $('ocResultBooth'), resLapse: $('ocResultLapse'),
    resProg: $('ocResProg'), resSt: $('ocResSt'),
    modeSec: $('ocModeSec'), modeSum: $('ocModeSum'),
    device: $('ocDevice'), res: $('ocRes'), resNote: $('ocResNote'), mirror: $('ocMirror'), mirrorSave: $('ocMirrorSave'),
    sensor: $('ocSensor'), sensorWrap: $('ocSensorWrap'), sensorNote: $('ocSensorNote'), flashWrap: $('ocFlashWrap'),
    manual: $('ocManual'), missing: $('ocMissing'), manReset: $('ocManReset'), manSum: $('ocManSum'), camSum: $('ocCamSum'),
    lookSum: $('ocLookSum'), smartSum: $('ocSmartSum'), smartState: $('ocSmartState'),
    rollGrid: $('ocRollGrid'), rollCount: $('ocRollCount'), rollEmpty: $('ocRollEmpty'), rollMem: $('ocRollMem'),
    dlAll: $('ocDlAll'), select: $('ocSelect'), dlSel: $('ocDlSel'), delSel: $('ocDelSel'), delAll: $('ocDelAll'),
    viewer: $('ocViewer'), vName: $('ocViewerName'), vMeta: $('ocViewerMeta'), vMedia: $('ocViewerMedia'),
};

const cam = new Camera({ video: el.video, slug: SLUG });
const roll = new Roll();
const usedNames = new Set();
let items = [];            // roll records, oldest first
let open = false;          // camera live
let mirrorPreview = S.mirrorFront;

/* ───────────────────────── sound ───────────────────────── */
let actx = null;
function audio() {
    if (!S.sound) return null;
    try {
        if (!actx) { const AC = window.AudioContext || window.webkitAudioContext; if (!AC) return null; actx = new AC(); }
        if (actx.state === 'suspended') actx.resume().catch(() => { });
        return actx.state === 'closed' ? null : actx;
    } catch (_) { return null; }
}
function beep(freq, dur, vol) {
    const a = audio(); if (!a) return;
    try {
        const o = a.createOscillator(), g = a.createGain(), t = a.currentTime;
        o.type = 'sine'; o.frequency.value = freq;
        g.gain.setValueAtTime(0.0001, t);
        g.gain.exponentialRampToValueAtTime(vol || 0.22, t + 0.01);
        g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
        o.connect(g); g.connect(a.destination); o.start(t); o.stop(t + dur + 0.02);
    } catch (_) { }
}
/** A mechanical shutter: two short band-passed noise clicks (curtain open, curtain close). */
function shutterSound(light) {
    const a = audio(); if (!a) return;
    try {
        const len = Math.round(a.sampleRate * 0.05);
        const buf = a.createBuffer(1, len, a.sampleRate);
        const d = buf.getChannelData(0);
        for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 3);
        const click = (at, vol) => {
            const s = a.createBufferSource(); s.buffer = buf;
            const f = a.createBiquadFilter(); f.type = 'bandpass'; f.frequency.value = 2600; f.Q.value = 0.9;
            const g = a.createGain(); g.gain.value = vol;
            s.connect(f); f.connect(g); g.connect(a.destination); s.start(a.currentTime + at);
        };
        click(0, light ? 0.5 : 0.9);
        if (!light) click(0.075, 0.6);
    } catch (_) { }
}

/* ───────────────────────── viewfinder layout & look ───────────────────────── */
function applyMirror() {
    el.video.classList.toggle('oc-mirror', mirrorPreview);
    el.assist.classList.toggle('oc-mirror', mirrorPreview);
}
function layoutVf() {
    const vw = cam.width, vh = cam.height;
    if (vw > 0 && vh > 0) el.vf.style.setProperty('--oc-ar', (vw / vh).toFixed(5));
    const r = el.vf.getBoundingClientRect();
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    for (const c of [el.guide, el.assist]) {
        const w = Math.max(1, Math.round(r.width * dpr)), h = Math.max(1, Math.round(r.height * dpr));
        if (c.width !== w || c.height !== h) { c.width = w; c.height = h; }
    }
    guideDirty();
}
let _noiseUrl = null;
function noiseTile() {
    if (_noiseUrl) return _noiseUrl;
    const c = document.createElement('canvas'); c.width = c.height = 128;
    const x = c.getContext('2d'), id = x.createImageData(128, 128), r = C.rng(9);
    for (let i = 0; i < id.data.length; i += 4) { const v = Math.round(r() * 255); id.data[i] = id.data[i + 1] = id.data[i + 2] = v; id.data[i + 3] = 255; }
    x.putImageData(id, 0, 0);
    _noiseUrl = c.toDataURL('image/png');
    return _noiseUrl;
}
function applyLook() {
    const f = C.FILTERS[S.look] || C.FILTERS.none;
    el.video.style.filter = C.cssFilter(S.look) === 'none' ? '' : C.cssFilter(S.look);
    // colour washes: one layer is enough for every look defined in core.js
    const wash = f.layers && f.layers[0];
    el.wash.style.background = wash ? wash[0] : '';
    el.wash.style.mixBlendMode = wash ? wash[1] : '';
    el.wash.hidden = !wash;
    el.vignette.style.background = f.vignette ? C.vignetteCss(f.vignette) : '';
    el.vignette.hidden = !f.vignette;
    if (f.grain) {
        el.grain.style.background = 'url(' + noiseTile() + ')';
        el.grain.style.mixBlendMode = 'overlay';
        el.grain.style.opacity = String(Math.min(0.5, f.grain * 4));
    }
    el.grain.hidden = !f.grain;
    document.querySelectorAll('#ocLooks .oc-look').forEach((b) => {
        const on = b.dataset.v === S.look;
        b.classList.toggle('on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    el.lookSum.textContent = T('f_' + S.look);
}

/* ───────────────────────── guide overlay: crop frame, grid, level ───────────────────────── */
let _guideRaf = 0;
let level = null;          // {mode, roll | x,y}
let smartBoxes = null;     // face boxes from the smile shutter (normalised, un-mirrored)
function guideDirty() { if (!_guideRaf) _guideRaf = requestAnimationFrame(() => { _guideRaf = 0; drawGuide(); }); }

/** The crop the preview shows: a preset, or the booth cell's exact shape. */
function currentAspect() {
    if (S.mode === 'booth') {
        const L = C.boothLayout(S.boothLayout, S.boothN, S.boothOut);
        return [L.cells[0].w, L.cells[0].h];
    }
    return S.aspect;
}
function cropOnScreen(W, H) {
    const vw = cam.width || 16, vh = cam.height || 9;
    const r = C.mapCrop(vw, vh, vw, vh, currentAspect(), 1);
    const sx = W / vw, sy = H / vh;
    return { x: r.x * sx, y: r.y * sy, w: r.w * sx, h: r.h * sy };
}
function drawGuide() {
    const c = el.guide, x = c.getContext('2d'), W = c.width, H = c.height;
    x.clearRect(0, 0, W, H);
    if (!open) return;
    const dpr = W / Math.max(1, el.vf.clientWidth);
    const r = cropOnScreen(W, H);
    const full = r.w >= W - 1 && r.h >= H - 1;
    if (!full) {
        x.save();
        x.fillStyle = 'rgba(0,0,0,0.58)';
        x.beginPath(); x.rect(0, 0, W, H); x.rect(r.x, r.y, r.w, r.h);
        x.fill('evenodd');
        x.strokeStyle = 'rgba(255,255,255,0.85)'; x.lineWidth = 1.5 * dpr;
        x.strokeRect(r.x + 0.5, r.y + 0.5, r.w - 1, r.h - 1);
        x.restore();
    }
    // grid inside the frame
    if (S.grid !== 'none') {
        x.save();
        x.strokeStyle = 'rgba(255,255,255,0.55)'; x.lineWidth = 1 * dpr;
        x.shadowColor = 'rgba(0,0,0,0.5)'; x.shadowBlur = 2 * dpr;
        const line = (x1, y1, x2, y2) => { x.beginPath(); x.moveTo(x1, y1); x.lineTo(x2, y2); x.stroke(); };
        const fr = S.grid === 'golden' ? [0.382, 0.618] : [1 / 3, 2 / 3];
        if (S.grid === 'thirds' || S.grid === 'golden') {
            for (const f of fr) { line(r.x + r.w * f, r.y, r.x + r.w * f, r.y + r.h); line(r.x, r.y + r.h * f, r.x + r.w, r.y + r.h * f); }
        } else if (S.grid === 'diag') {
            line(r.x, r.y, r.x + r.w, r.y + r.h); line(r.x + r.w, r.y, r.x, r.y + r.h);
            for (const f of [1 / 3, 2 / 3]) { line(r.x + r.w * f, r.y, r.x + r.w * f, r.y + r.h); line(r.x, r.y + r.h * f, r.x + r.w, r.y + r.h * f); }
        } else if (S.grid === 'center') {
            const cx = r.x + r.w / 2, cy = r.y + r.h / 2, s = Math.min(r.w, r.h) * 0.06;
            line(cx - s, cy, cx + s, cy); line(cx, cy - s, cx, cy + s);
        }
        x.restore();
    }
    // level
    if (S.level && level) {
        const cx = W / 2, cy = H / 2;
        x.save();
        if (level.mode === 'horizon') {
            const ok = C.isLevel(level.roll, 1);
            const len = Math.min(W, H) * 0.42;
            x.translate(cx, cy);
            x.rotate(-level.roll * Math.PI / 180);
            x.strokeStyle = ok ? '#34d399' : 'rgba(255,255,255,0.9)';
            x.lineWidth = (ok ? 3 : 2) * dpr;
            x.shadowColor = 'rgba(0,0,0,0.6)'; x.shadowBlur = 3 * dpr;
            x.beginPath(); x.moveTo(-len, 0); x.lineTo(-len * 0.18, 0); x.moveTo(len * 0.18, 0); x.lineTo(len, 0); x.stroke();
            x.rotate(level.roll * Math.PI / 180);
            x.strokeStyle = 'rgba(255,255,255,0.45)'; x.lineWidth = 1 * dpr;
            x.beginPath(); x.moveTo(-len * 0.14, 0); x.lineTo(len * 0.14, 0); x.stroke();
            x.fillStyle = ok ? '#34d399' : '#fff';
            x.font = '700 ' + Math.round(13 * dpr) + 'px system-ui, sans-serif';
            x.textAlign = 'center';
            const a = Math.abs(level.roll) > 45 ? Math.abs(Math.abs(level.roll) - 90) : Math.abs(level.roll);
            x.fillText(a.toFixed(1) + '°', 0, -12 * dpr);
        } else {
            const R = Math.min(W, H) * 0.12;
            const ok = Math.hypot(level.x, level.y) < 0.08;
            x.strokeStyle = 'rgba(255,255,255,0.8)'; x.lineWidth = 2 * dpr;
            x.beginPath(); x.arc(cx, cy, R, 0, Math.PI * 2); x.stroke();
            x.beginPath(); x.arc(cx, cy, R * 0.15, 0, Math.PI * 2); x.stroke();
            x.fillStyle = ok ? '#34d399' : '#fde047';
            x.beginPath(); x.arc(cx + level.x * R, cy + level.y * R, R * 0.13, 0, Math.PI * 2); x.fill();
        }
        x.restore();
    }
    // faces the smile shutter is watching
    if (smartBoxes && smartBoxes.length) {
        x.save();
        x.strokeStyle = 'rgba(52,211,153,0.9)'; x.lineWidth = 2 * dpr;
        for (const b of smartBoxes) {
            const bx = mirrorPreview ? (1 - b.x - b.w) : b.x;
            x.strokeRect(bx * W, b.y * H, b.w * W, b.h * H);
        }
        x.restore();
    }
}

/* ───────────────────────── analysis: histogram, zebra, peaking ───────────────────────── */
const ana = { c: document.createElement('canvas'), zc: document.createElement('canvas'), pc: document.createElement('canvas'), raf: 0, last: 0, pattern: null };
function analysisOn() { return open && (S.hist || S.zebra || S.peak); }
function startAnalysis() {
    el.histo.hidden = !(open && S.hist);
    if (!(open && S.hist)) el.clip.hidden = true;
    if (!analysisOn()) {
        if (ana.raf) cancelAnimationFrame(ana.raf); ana.raf = 0;
        const x = el.assist.getContext('2d'); x.clearRect(0, 0, el.assist.width, el.assist.height);
        return;
    }
    if (ana.raf) return;
    const step = (now) => {
        ana.raf = requestAnimationFrame(step);
        if (!analysisOn()) { cancelAnimationFrame(ana.raf); ana.raf = 0; startAnalysis(); return; }
        if (now - ana.last < 80 || document.hidden) return;
        ana.last = now;
        try { analyse(); } catch (_) { }
    };
    ana.raf = requestAnimationFrame(step);
}
function stripePattern(ctx) {
    const p = document.createElement('canvas'); p.width = p.height = 16;
    const x = p.getContext('2d');
    x.strokeStyle = 'rgba(0,0,0,0.85)'; x.lineWidth = 4;
    x.beginPath(); x.moveTo(-4, 20); x.lineTo(20, -4); x.stroke();
    x.strokeStyle = 'rgba(255,255,255,0.9)';
    x.beginPath(); x.moveTo(4, 20); x.lineTo(20, 4); x.moveTo(-4, 12); x.lineTo(12, -4); x.stroke();
    return ctx.createPattern(p, 'repeat');
}
const PEAK_RGB = { red: [255, 40, 40], yellow: [255, 230, 0], blue: [40, 160, 255] };
function analyse() {
    const vw = cam.width, vh = cam.height;
    if (!(vw > 0)) return;
    const aw = S.peak ? 480 : 256, ah = Math.max(1, Math.round(aw * vh / vw));
    const c = ana.c;
    if (c.width !== aw || c.height !== ah) { c.width = aw; c.height = ah; }
    const x = c.getContext('2d', { willReadFrequently: true });
    const look = C.FILTERS[S.look] || C.FILTERS.none;
    x.filter = (look.ops.length && ctxFilterWorks()) ? C.cssFilter(S.look) : 'none';
    const drawn = window.__drawCameraFrame(x, el.video, 0, 0, aw, ah);
    x.filter = 'none';
    if (!drawn) return;   // no frame this tick
    const id = x.getImageData(0, 0, aw, ah);
    if (look.ops.length && !ctxFilterWorks()) C.applyOps(id.data, look.ops);
    if (S.hist) drawHisto(C.histogram(id.data));
    const A = el.assist, ax = A.getContext('2d');
    ax.clearRect(0, 0, A.width, A.height);
    if (S.zebra) {
        const m = C.zebraMask(id.data, Math.round(S.zebraThr * 2.55));
        const zc = ana.zc; if (zc.width !== aw || zc.height !== ah) { zc.width = aw; zc.height = ah; }
        const zx = zc.getContext('2d'); const zd = zx.createImageData(aw, ah);
        for (let i = 0; i < m.length; i++) if (m[i]) zd.data[i * 4 + 3] = 255;
        zx.putImageData(zd, 0, 0);
        // stripes at screen resolution, cut to the mask
        const L = ana.lay || (ana.lay = document.createElement('canvas'));
        if (L.width !== A.width || L.height !== A.height) { L.width = A.width; L.height = A.height; ana.pattern = null; }
        const lx = L.getContext('2d');
        lx.globalCompositeOperation = 'source-over';
        lx.clearRect(0, 0, L.width, L.height);
        if (!ana.pattern) ana.pattern = stripePattern(lx);
        lx.fillStyle = ana.pattern; lx.fillRect(0, 0, L.width, L.height);
        lx.globalCompositeOperation = 'destination-in';
        lx.imageSmoothingEnabled = true;
        lx.drawImage(zc, 0, 0, L.width, L.height);
        lx.globalCompositeOperation = 'source-over';
        ax.drawImage(L, 0, 0);
    }
    if (S.peak) {
        const { mask } = C.peakingMask(id.data, aw, ah, S.peakSens / 100);
        const pc = ana.pc; if (pc.width !== aw || pc.height !== ah) { pc.width = aw; pc.height = ah; }
        const px = pc.getContext('2d'); const pd = px.createImageData(aw, ah);
        const col = PEAK_RGB[S.peakColor] || PEAK_RGB.red;
        for (let i = 0; i < mask.length; i++) if (mask[i]) { const j = i * 4; pd.data[j] = col[0]; pd.data[j + 1] = col[1]; pd.data[j + 2] = col[2]; pd.data[j + 3] = 255; }
        px.putImageData(pd, 0, 0);
        ax.imageSmoothingEnabled = false;
        ax.drawImage(pc, 0, 0, A.width, A.height);
        ax.imageSmoothingEnabled = true;
    }
}
function drawHisto(h) {
    const c = el.histo, x = c.getContext('2d'), W = c.width, H = c.height;
    x.clearRect(0, 0, W, H);
    let max = 1;
    for (let i = 2; i < 254; i++) max = Math.max(max, h.l[i], h.r[i], h.g[i], h.b[i]);
    const path = (arr) => {
        x.beginPath(); x.moveTo(0, H);
        for (let i = 0; i < 256; i++) x.lineTo(i / 255 * W, H - Math.min(1, arr[i] / max) * (H - 4));
        x.lineTo(W, H); x.closePath();
    };
    x.fillStyle = 'rgba(226,232,240,0.55)'; path(h.l); x.fill();
    x.globalCompositeOperation = 'lighter';
    x.fillStyle = 'rgba(239,68,68,0.45)'; path(h.r); x.fill();
    x.fillStyle = 'rgba(34,197,94,0.45)'; path(h.g); x.fill();
    x.fillStyle = 'rgba(59,130,246,0.5)'; path(h.b); x.fill();
    x.globalCompositeOperation = 'source-over';
    const pct = h.clipped * 100;
    if (pct >= 0.5) { el.clip.hidden = false; el.clip.textContent = T('clip', { p: pct < 10 ? pct.toFixed(1) : Math.round(pct) }); }
    else el.clip.hidden = true;
}

/* ───────────────────────── level (DeviceOrientation) ───────────────────────── */
let _levelOn = false, _levelSeen = false, _levelTimer = 0;
function onOrient(e) {
    const ang = (screen.orientation && typeof screen.orientation.angle === 'number') ? screen.orientation.angle : (typeof window.orientation === 'number' ? window.orientation : 0);
    const r = C.levelFrom(e.beta, e.gamma, ang);
    if (!r) return;
    _levelSeen = true;
    const note = $('ocLevelNote'); if (!note.hidden) note.hidden = true;
    level = r;
    guideDirty();
}
async function setLevel(on) {
    const note = $('ocLevelNote');
    note.hidden = true;
    if (!on) { if (_levelOn) window.removeEventListener('deviceorientation', onOrient); _levelOn = false; level = null; guideDirty(); return; }
    if (typeof window.DeviceOrientationEvent === 'undefined') { note.textContent = T('level_none'); note.hidden = false; return; }
    // iOS asks for the motion sensor on a tap; Chrome now has the same call and
    // answers «denied» on a desktop that simply has no sensor. So the verdict is
    // taken from the readings, not from the answer: listen either way, and if
    // nothing arrives, a phone was refused and a computer has no sensor.
    let refused = false;
    try {
        if (typeof DeviceOrientationEvent.requestPermission === 'function') {
            refused = (await DeviceOrientationEvent.requestPermission()) !== 'granted';
        }
    } catch (_) { refused = true; }
    if (!_levelOn) window.addEventListener('deviceorientation', onOrient);
    _levelOn = true; _levelSeen = false;
    clearTimeout(_levelTimer);
    _levelTimer = setTimeout(() => {
        if (_levelOn && !_levelSeen) { note.textContent = T(refused && isMobile ? 'level_denied' : 'level_none'); note.hidden = false; }
    }, 1500);
}

/* ───────────────────────── camera open / close ───────────────────────── */
let _opening = false;
async function openCamera(opts) {
    if (_opening) return;
    _opening = true;
    const o = opts || {};
    el.fail.hidden = true;
    el.start.disabled = true;
    stage('camera_request');
    const res = C.RES_PRESETS[S.res] || C.RES_PRESETS.max;
    const want = { res, button: o.button };
    if (o.deviceId) want.deviceId = o.deviceId;
    else if (o.facingMode) want.facingMode = o.facingMode;
    else if (S.device) want.deviceId = S.device;
    else if (isMobile) want.facingMode = 'environment';
    try {
        try { await cam.open(want); }
        catch (e) {
            // a remembered camera that is gone (unplugged, another browser profile): once without it
            if (want.deviceId && !o.deviceId && (e.code === 'not_found' || e.code === 'generic')) {
                save('device', '');
                delete want.deviceId;
                await cam.open(want);
            } else throw e;
        }
        open = true;
        el.idle.hidden = true;
        el.fail.hidden = true;
        el.shutter.disabled = false;
        el.flip.disabled = false;
        if (cam.track) cam.track.addEventListener('ended', onTrackEnded, { once: true });
        const env = cam.facing === 'environment';
        mirrorPreview = env ? false : S.mirrorFront;
        el.mirror.checked = mirrorPreview;
        applyMirror();
        layoutVf();
        await fillDevices();
        buildManual();
        describeCamera();
        startAnalysis();
        if (S.smart !== 'off') setSmart(S.smart);
        if (window.__wakeLock) { try { window.__wakeLock.request(); } catch (_) { } }
        stage('camera_on', { w: cam.width, h: cam.height, still: cam.canTakePhoto() ? 1 : 0, facing: cam.facing || 'na' });
        feat('camera_on', { w: cam.width, h: cam.height, fps: Math.round(cam.settings.frameRate || 0), still: cam.stillSize() ? cam.stillSize().w : 0, res: S.res });
    } catch (e) {
        open = false;
        el.shutter.disabled = true;
        showFail(cam.errorMessage(e));
        diag({ issue: 'camera_open_failed', code: (e && e.code) || '', cause: (e && e.causeName) || '' });
    } finally {
        _opening = false;
        el.start.disabled = false;
    }
}
function showFail(msg) {
    el.idle.hidden = true;
    el.failText.textContent = msg;
    el.fail.hidden = false;
}
function onTrackEnded() {
    if (!open) return;
    open = false;
    el.shutter.disabled = true;
    cancelSeq();
    showFail(T('camera_lost'));
    guideDirty(); startAnalysis();
}
async function fillDevices() {
    const list = await cam.devices();
    const cur = cam.settings.deviceId || '';
    const sel = el.device;
    sel.textContent = '';
    for (const d of list) {
        const opt = document.createElement('option');
        opt.value = d.deviceId; opt.textContent = d.label;
        if (d.deviceId === cur) opt.selected = true;
        sel.appendChild(opt);
    }
    if (!list.length) { const o = document.createElement('option'); o.value = ''; o.textContent = T('camera_default'); sel.appendChild(o); }
    el.flip.hidden = false;
    el.flip.disabled = list.length < 2 && !isMobile;
    el._devices = list;
}
function describeCamera() {
    const s = cam.settings, w = cam.width, h = cam.height;
    const fps = Math.round(s.frameRate || 0);
    const parts = [T('info_stream', { w, h, fps: fps || '—' })];
    const st = cam.stillSize();
    const stillBigger = st && st.w * st.h > w * h * 1.05;
    if (S.sensor && stillBigger) parts.push(T('info_still', { w: st.w, h: st.h, mp: C.megapixels(st.w, st.h) }));
    if (cam.label) parts.push(cam.label);
    el.info.textContent = parts.join(' · ');
    el.camSum.textContent = w + '×' + h;
    el.resNote.textContent = (S.res === 'max' && fps > 0 && fps < 20) ? T('res_slow', { fps }) : '';
    el.resNote.hidden = !el.resNote.textContent;
    // The still is only worth its slower shutter when it has more pixels than
    // the stream (a desktop webcam often reports the stream size itself).
    const stillHelps = cam.canTakePhoto() && (!st || stillBigger);
    el.sensorWrap.hidden = !stillHelps;
    el.sensorNote.hidden = !stillHelps;
    const fm = cam.flashModes();
    el.flashWrap.hidden = !(fm.indexOf('flash') >= 0);
}

/* ───────────────────────── manual controls ───────────────────────── */
const MAN_GROUPS = [
    ['mg_exposure', ['exposureMode', 'exposureCompensation', 'exposureTime', 'iso']],
    ['mg_focus', ['focusMode', 'focusDistance']],
    ['mg_wb', ['whiteBalanceMode', 'colorTemperature']],
    ['mg_zoom', ['zoom']],
    ['mg_torch', ['torch']],
    ['mg_image', ['brightness', 'contrast', 'saturation', 'sharpness']],
];
const MODE_KEYS = { exposureMode: 'exposure', focusMode: 'focus', whiteBalanceMode: 'wb' };
const NEEDS_MANUAL = { exposureTime: ['exposureMode', 'manual'], iso: ['exposureMode', 'manual'], focusDistance: ['focusMode', 'manual'], colorTemperature: ['whiteBalanceMode', 'manual'] };
const LOG_KEYS = { exposureTime: 1, iso: 1 };
const _applyTimers = {};
function fmtVal(key, v) {
    if (v == null || !isFinite(v)) return '—';
    switch (key) {
        case 'exposureTime': return C.formatShutter(v);
        case 'iso': return 'ISO ' + Math.round(v);
        case 'exposureCompensation': return (v > 0 ? '+' : v < 0 ? '−' : '') + Math.abs(Math.round(v * 10) / 10) + ' EV';
        case 'colorTemperature': return Math.round(v) + ' K';
        case 'zoom': return (Math.round(v * 10) / 10) + '×';
        case 'focusDistance': return v >= 1 ? (Math.round(v * 10) / 10) + ' m' : Math.round(v * 100) + ' cm';
        default: return String(Math.round(v * 100) / 100);
    }
}
function buildManual() {
    const caps = cam.caps || {}, st = cam.settings || {};
    const box = el.manual;
    box.textContent = '';
    let shown = 0;
    const missing = [];
    for (const [gkey, keys] of MAN_GROUPS) {
        let any = false;
        for (const key of keys) {
            const cap = caps[key];
            if (MODE_KEYS[key]) {
                if (!Array.isArray(cap) || cap.length < 2) continue;
                any = true; shown++;
                box.appendChild(modeControl(key, cap, st[key]));
            } else if (key === 'torch') {
                if (cap !== true && !(Array.isArray(cap) && cap.indexOf(true) >= 0)) continue;
                any = true; shown++;
                box.appendChild(torchControl(!!st.torch));
            } else {
                if (!C.usableRange(cap)) continue;
                any = true; shown++;
                box.appendChild(rangeControl(key, cap, st[key]));
            }
        }
        if (!any) missing.push(T(gkey));
    }
    const pois = cam.supportsPointsOfInterest() && Array.isArray(caps.focusMode) && caps.focusMode.length;
    if (pois) { const p = document.createElement('p'); p.className = 'oc-hint'; p.textContent = T('tap_focus_hint'); box.appendChild(p); }
    el.missing.hidden = !open;
    el.missing.textContent = shown ? (missing.length ? T('m_missing', { list: missing.join(', ') }) : '') : T('m_none');
    el.missing.hidden = !el.missing.textContent;
    el.manReset.hidden = !shown;
    el.manSum.textContent = open ? String(shown) : '';
}
function modeControl(key, list, cur) {
    const f = document.createElement('div'); f.className = 'oc-f';
    const id = 'ocM_' + key;
    const lab = document.createElement('label'); lab.className = 'oc-l'; lab.htmlFor = id; lab.textContent = T('m_' + MODE_KEYS[key] + '_mode');
    const sel = document.createElement('select'); sel.id = id;
    const names = { continuous: T('m_auto'), manual: T('m_manual'), 'single-shot': T('m_once'), none: T('m_off') };
    for (const m of list) { const o = document.createElement('option'); o.value = m; o.textContent = names[m] || m; if (m === cur) o.selected = true; sel.appendChild(o); }
    sel.addEventListener('change', async () => {
        const ok = await cam.apply({ [key]: sel.value });
        if (!ok) toast(T('m_failed'), 'warning');
        feat('manual', { k: key, v: sel.value, ok: ok ? 1 : 0 });
        syncManual();
    });
    f.append(lab, sel);
    return f;
}
function torchControl(on) {
    const lab = document.createElement('label'); lab.className = 'oc-sw';
    const cb = document.createElement('input'); cb.type = 'checkbox'; cb.id = 'ocM_torch'; cb.checked = on;
    cb.addEventListener('change', async () => {
        const ok = await cam.apply({ torch: cb.checked });
        if (!ok) { cb.checked = !cb.checked; toast(T('m_failed'), 'warning'); }
        feat('manual', { k: 'torch', v: cb.checked ? 1 : 0, ok: ok ? 1 : 0 });
    });
    lab.append(cb, document.createTextNode(' ' + T('m_torch')));
    return lab;
}
function rangeControl(key, cap, cur) {
    const f = document.createElement('div'); f.className = 'oc-f';
    const id = 'ocM_' + key;
    const lab = document.createElement('label'); lab.className = 'oc-l'; lab.htmlFor = id;
    const name = document.createElement('span'); name.textContent = T('m_' + key);
    const val = document.createElement('b'); val.id = id + 'v'; val.textContent = fmtVal(key, cur);
    lab.append(name, val);
    const r = document.createElement('input'); r.type = 'range'; r.id = id;
    const log = LOG_KEYS[key] && cap.min > 0;
    if (log) { r.min = 0; r.max = 1000; r.step = 1; r.value = C.valueToLog(cur != null ? cur : cap.min, cap.min, cap.max); }
    else { r.min = cap.min; r.max = cap.max; r.step = cap.step > 0 ? cap.step : (cap.max - cap.min) / 100; r.value = cur != null ? cur : cap.min; }
    r.dataset.key = key;
    r.addEventListener('input', () => {
        const v = C.snapStep(log ? C.logToValue(+r.value, cap.min, cap.max) : +r.value, cap);
        val.textContent = fmtVal(key, v);
        clearTimeout(_applyTimers[key]);
        _applyTimers[key] = setTimeout(async () => {
            const set = { [key]: v };
            const need = NEEDS_MANUAL[key];
            if (need && (cam.caps[need[0]] || []).indexOf(need[1]) >= 0) set[need[0]] = need[1];
            const ok = await cam.apply(set);
            if (!ok) toast(T('m_failed'), 'warning');
            feat('manual', { k: key, ok: ok ? 1 : 0 });
            syncManual();
        }, 70);
    });
    f.append(lab, r);
    return f;
}
/** Read back what the camera actually did and show it (a request is not a result). */
function syncManual() {
    const st = cam.settings || {};
    for (const [, keys] of MAN_GROUPS) {
        for (const key of keys) {
            const n = $('ocM_' + key);
            if (!n) continue;
            if (n.tagName === 'SELECT') { if (st[key]) n.value = st[key]; }
            else if (n.type === 'checkbox') n.checked = !!st[key];
            else {
                const v = $('ocM_' + key + 'v');
                if (st[key] != null) {
                    const cap = cam.caps[key];
                    if (LOG_KEYS[key] && cap && cap.min > 0) n.value = C.valueToLog(st[key], cap.min, cap.max); else n.value = st[key];
                    if (v) v.textContent = fmtVal(key, st[key]);
                }
            }
        }
    }
    describeCamera();
}
async function resetManual() {
    const caps = cam.caps || {}, init = cam.initial || {};
    const set = {};
    for (const k of Object.keys(MODE_KEYS)) if (Array.isArray(caps[k]) && caps[k].indexOf('continuous') >= 0) set[k] = 'continuous';
    for (const k of ['exposureCompensation', 'zoom', 'brightness', 'contrast', 'saturation', 'sharpness']) if (C.usableRange(caps[k]) && init[k] != null) set[k] = init[k];
    if (caps.torch) set.torch = false;
    await cam.apply(set);
    buildManual();
    feat('manual_reset');
}

/* ───────────────────────── capture ───────────────────────── */
let seq = null;
function busy() { return !!seq; }
function setShutter() {
    el.shutter.dataset.state = seq ? 'stop' : 'idle';
    el.shutter.dataset.mode = S.mode;
    el.shutter.setAttribute('aria-label', seq ? T('stop') : (S.mode === 'timelapse' ? T('tl_start') : T('shutter')));
    el.shutter.title = el.shutter.getAttribute('aria-label');
    const lock = !!seq;
    el.modes.querySelectorAll('.oc-mode').forEach((b) => { b.disabled = lock; });
    el.flip.disabled = lock || !open;
    el.device.disabled = lock; el.res.disabled = lock;
    remoteState();
}
async function runSeq(kind, fn) {
    if (seq) return;
    const ac = new AbortController();
    seq = { ac, kind };
    setShutter();
    smart.hold(true);
    try { await fn(ac.signal); }
    catch (e) {
        if (e && e.noFrame) {
            // the camera skipped a frame (iOS interrupts capture): words, not the «no-frame» token
            toast(window.__cameraNoFrame(), 'warning');
            diag({ issue: 'shot_no_frame', mode: kind });
        } else if (!(e && e.name === 'AbortError')) {
            toast(T('shot_failed', { err: (e && e.message) || String(e) }), 'error');
            diag({ issue: 'shot_failed', mode: kind, msg: String((e && e.message) || e).slice(0, 120) });
        }
    } finally {
        seq = null;
        hideCount();
        setBadge(null);
        setShutter();
        smart.hold(false);
        screenLight(false);
    }
}
function cancelSeq() { if (seq) seq.ac.abort(); }

function showCount(n, sub) {
    el.count.hidden = false;
    el.count.textContent = String(n);
    if (sub) { const s = document.createElement('small'); s.textContent = sub; el.count.appendChild(s); }
    el.count.classList.remove('tick'); void el.count.offsetWidth; el.count.classList.add('tick');
}
function hideCount() { el.count.hidden = true; el.count.textContent = ''; remoteSend(['cd', 0]); }
async function countdown(sec, sig, sub) {
    for (let s = sec; s >= 1; s--) {
        showCount(s, sub);
        remoteSend(['cd', s]);
        if (s <= 2) { beep(1320, 0.09); setTimeout(() => beep(1320, 0.07), 180); } else beep(880, 0.1);
        await sleep(1000, sig);
    }
    hideCount();
}
function blink() {
    if (!S.blink) return;
    el.flash.classList.remove('on'); void el.flash.offsetWidth; el.flash.classList.add('on');
}
let _light = null;
async function screenLight(on) {
    if (!on) { if (_light) { _light.remove(); _light = null; } return; }
    if (_light) return;
    _light = document.createElement('div');
    _light.className = 'oc-screenlight';
    // in full screen only the full-screen element is painted
    (document.fullscreenElement || document.webkitFullscreenElement || document.body).appendChild(_light);
    await sleep(450);       // give auto-exposure a moment to settle on the lit face
}
function setBadge(parts) {
    el.badge.textContent = '';
    if (!parts) return;
    for (const p of parts) {
        if (!p) continue;
        const s = document.createElement('span');
        s.className = 'oc-pill' + (p.rec ? ' rec' : '');
        s.textContent = p.text;
        el.badge.appendChild(s);
    }
}

/** The picture at the moment of the press: a stream frame now, a sensor still if it is really better. */
async function capturePicture(allowStill) {
    const frame = cam.grabFrame();
    if (!frame) { const e = new Error('no-frame'); e.noFrame = true; throw e; }
    shutterSound(!allowStill);
    blink();
    let src = frame, w = frame.width, h = frame.height, via = 'frame';
    const st = cam.stillSize();
    if (allowStill && S.sensor && cam.canTakePhoto() && (!st || st.w * st.h > w * h * 1.05)) {
        const bmp = await cam.takeStill({ fillLight: S.flashMode !== 'off' ? S.flashMode : null });
        if (bmp && bmp.width * bmp.height > w * h * 1.05 && C.sameOrientation(w, h, bmp.width, bmp.height)) {
            src = bmp; w = bmp.width; h = bmp.height; via = 'still';
        } else if (bmp) {
            diag({ issue: 'still_not_used', sw: bmp.width, sh: bmp.height, fw: w, fh: h });
            if (bmp.close) bmp.close();
        }
    }
    return { src, w, h, via, streamW: frame.width, streamH: frame.height };
}
function developed(p, date, aspect) {
    return develop(p.src, p.w, p.h, {
        streamW: p.streamW, streamH: p.streamH, aspect: aspect || S.aspect, mirror: S.mirrorSave && mirrorPreview,
        filter: S.look, date: S.date ? date : null, seed: (date.getTime() % 100000) | 0,
    });
}
const _completed = new Set();
let _webpNoted = false;
/** A JPEG gets the capture date (and the exposure the camera reports) like a camera writes it. */
function stampExif(blob, canvas, date, withExposure) {
    const st = cam.settings || {};
    return withExif(blob, {
        date, width: canvas.width, height: canvas.height,
        exposureS: withExposure && st.exposureTime > 0 ? st.exposureTime / 10000 : 0,
        iso: withExposure && st.iso > 0 ? st.iso : 0,
    });
}
async function saveShot(canvas, kind, date, seqNo) {
    let { blob, fmt } = await encode(canvas, S.format, S.quality / 100);
    blob = await stampExif(blob, canvas, date, kind !== 'night');
    if (fmt !== S.format && S.format === 'webp' && !_webpNoted) { _webpNoted = true; toast(T('webp_fallback'), 'warning'); }
    return addToRoll(blob, fmt, canvas, kind, date, seqNo);
}
async function addToRoll(blob, fmt, preview, kind, date, seqNo) {
    const thumb = await thumbnail(preview, preview.width, preview.height, 320);
    const rec = {
        blob, thumb, name: C.photoName(date, kind, fmt, seqNo == null ? null : seqNo, usedNames),
        type: blob.type, w: preview.width, h: preview.height, size: blob.size, ts: Date.now(), kind,
    };
    const id = await roll.add(rec);
    rec.id = id;
    items.push(rec);
    renderRoll();
    if (!roll.durable) el.rollMem.hidden = false;
    remoteShot(thumb);
    if (!_completed.has(kind)) {
        _completed.add(kind);
        try { if (window.__aeToolComplete) window.__aeToolComplete({ kind, fmt, w: rec.w, h: rec.h }); } catch (_) { }
    }
    return rec;
}

async function shootPhoto(trigger) {
    await runSeq('photo', async (sig) => {
        const useTimer = trigger !== 'smile' ? (trigger === 'palm' ? Math.max(3, S.timer) : S.timer) : 0;
        if (useTimer) await countdown(useTimer, sig);
        if (S.screenLight) await screenLight(true);
        const date = new Date();
        const p = await capturePicture(true);
        screenLight(false);
        setBadge([{ text: T('processing') }]);
        const cv = developed(p, date);
        const rec = await saveShot(cv, trigger === 'smile' ? 'smile' : 'photo', date);
        if (p.src.close) p.src.close();
        stage('shot');
        feat('shot', { mode: 'photo', trig: trigger, via: p.via, look: S.look, aspect: S.aspect, fmt: S.format, w: rec.w, h: rec.h, timer: useTimer });
    });
}
async function shootBurst(trigger) {
    await runSeq('burst', async (sig) => {
        if (S.timer && trigger !== 'smile') await countdown(trigger === 'palm' ? Math.max(3, S.timer) : S.timer, sig);
        const n = S.burstN, iv = Math.max(100, S.burstIv * 100);
        const shots = [];
        const date0 = new Date();
        let t = performance.now();
        for (let i = 0; i < n; i++) {
            if (sig.aborted) break;
            setBadge([{ text: T('burst_progress', { i: i + 1, n }), rec: true }]);
            const bmp = await cam.grabBitmap();
            shutterSound(true); blink();
            shots.push({ src: bmp, w: bmp.width, h: bmp.height, date: new Date() });
            t += iv;
            const wait = t - performance.now();
            if (i < n - 1 && wait > 0) await sleep(wait, sig).catch(() => { });
        }
        // save what was taken even if stopped half-way
        for (let i = 0; i < shots.length; i++) {
            setBadge([{ text: T('saving_progress', { i: i + 1, n: shots.length }) }]);
            const s = shots[i];
            const cv = developed({ src: s.src, w: s.w, h: s.h, streamW: s.w, streamH: s.h }, s.date);
            await saveShot(cv, 'burst', date0, i + 1);
            if (s.src.close) s.src.close();
        }
        stage('shot');
        feat('shot', { mode: 'burst', trig: trigger, n: shots.length, iv, look: S.look, fmt: S.format });
        toast(T('saved_n', { n: shots.length }), 'success');
    });
}
async function shootNight() {
    await runSeq('night', async (sig) => {
        if (S.timer) await countdown(S.timer, sig);
        const date = new Date();
        const n = S.nightN;
        setBadge([{ text: T('night_progress', { p: 0 }), rec: true }]);
        shutterSound(true);
        const c = await stackFrames(el.video, n, {
            signal: sig, maxSide: 2560, gain: 1 + S.nightBoost * 0.15, lift: S.nightBoost * 0.05,
            onProgress: (f) => setBadge([{ text: T('night_progress', { p: Math.round(f * 100) }), rec: true }]),
        });
        blink(); shutterSound(false);
        setBadge([{ text: T('processing') }]);
        const cv = developed({ src: c, w: c.width, h: c.height, streamW: c.width, streamH: c.height }, date);
        const rec = await saveShot(cv, 'night', date);
        feat('shot', { mode: 'night', n, boost: S.nightBoost, w: rec.w, h: rec.h });
    });
}

/* ───────────────────────── photo booth ───────────────────────── */
let lastBooth = null;      // {blob, name, layout, output}
let kioskOn = false, kioskTimer = 0;
function boothDateLine(d) {
    try { return new Intl.DateTimeFormat(CFG.locale || undefined, { day: 'numeric', month: 'long', year: 'numeric' }).format(d); }
    catch (_) { return d.toLocaleDateString(); }
}
async function shootBooth(trigger) {
    await runSeq('booth', async (sig) => {
        const n = S.boothN;
        const L = C.boothLayout(S.boothLayout, n, S.boothOut);
        const cell = [L.cells[0].w, L.cells[0].h];
        const shots = [];
        const date0 = new Date();
        if (kioskOn) { el.kioskGo.hidden = true; }
        for (let i = 0; i < n; i++) {
            setBadge([{ text: T('booth_progress', { i: i + 1, n }), rec: true }]);
            await countdown(S.boothCd, sig, i === 0 ? T('booth_ready') : T('booth_progress', { i: i + 1, n }));
            if (S.screenLight) await screenLight(true);
            const d = new Date();
            const p = await capturePicture(false);
            screenLight(false);
            const cv = developed(p, d, cell);
            shots.push({ src: cv, w: cv.width, h: cv.height });
            if (S.boothKeep) await saveShot(cv, 'photo', d);
            if (i < n - 1) await sleep(800, sig);
        }
        setBadge([{ text: T('processing') }]);
        const sheet = composeBooth(shots, {
            layout: S.boothLayout, output: S.boothOut, theme: S.boothTheme,
            caption: S.boothCap, dateLine: S.boothDate ? boothDateLine(date0) : '',
        });
        const fmt = S.format === 'png' ? 'png' : 'jpeg';
        let { blob } = await encode(sheet, fmt, 0.95);
        blob = await stampExif(blob, sheet, date0, false);
        const rec = await addToRoll(blob, fmt, sheet, 'booth', date0);
        lastBooth = { blob, name: rec.name, layout: S.boothLayout, output: S.boothOut, w: sheet.width, h: sheet.height };
        showBoothResult(blob, sheet);
        stage('shot');
        feat('shot', { mode: 'booth', n, layout: S.boothLayout, out: S.boothOut, theme: S.boothTheme, trig: trigger, kiosk: kioskOn ? 1 : 0, cap: S.boothCap ? 1 : 0 });
    });
    if (kioskOn) kioskAfter();
}
let _resUrl = null;
function setResultImage(blob) {
    if (_resUrl) URL.revokeObjectURL(_resUrl);
    try { if (window.__skipBlobAutoTrack) window.__skipBlobAutoTrack(blob); } catch (_) { }
    _resUrl = URL.createObjectURL(blob);
    el.resultImg.src = _resUrl;
    el.resultImg.hidden = false;
}
function showBoothResult(blob, sheet) {
    el.result.hidden = false;
    el.resBooth.hidden = false; el.resLapse.hidden = true; el.resProg.hidden = true;
    el.resultH.textContent = T('res_booth_title', { w: sheet.width, h: sheet.height });
    const [wi, hi] = printInches(S.boothLayout, S.boothOut);
    el.resSt.textContent = T('res_booth_size', { w: sheet.width, h: sheet.height, wi, hi, wc: Math.round(wi * 2.54), hc: Math.round(hi * 2.54) });
    setResultImage(blob);
    if (kioskOn) {
        const u = _resUrl;
        el.kioskImg.src = u;
        el.kioskRes.hidden = false;
    }
}
function kioskAfter() {
    if (!kioskOn) return;
    let left = 10;
    const tick = () => {
        if (!kioskOn) return;
        el.kioskMsg.textContent = T('kiosk_again', { s: left });
        if (left-- <= 0) { kioskReady(); return; }
        kioskTimer = setTimeout(tick, 1000);
    };
    if (el.kioskRes.hidden) { kioskReady(); return; }
    tick();
}
function kioskReady() {
    clearTimeout(kioskTimer);
    el.kioskRes.hidden = true;
    el.kioskGo.hidden = false;
    try { el.kioskGo.focus({ preventScroll: true }); } catch (_) { }
}
async function enterKiosk() {
    if (!open) { await openCamera({ button: $('ocKioskBtn') }); if (!open) return; }
    setMode('booth');
    kioskOn = true;
    el.kiosk.hidden = false;
    kioskReady();
    await goFullscreen(true);
    if (window.__wakeLock) { try { window.__wakeLock.request(); } catch (_) { } }
    feat('kiosk', { on: 1, layout: S.boothLayout });
}
function exitKiosk() {
    if (!kioskOn) return;
    kioskOn = false;
    clearTimeout(kioskTimer);
    cancelSeq();
    el.kiosk.hidden = true;
    goFullscreen(false);
    feat('kiosk', { on: 0 });
}

/* ───────────────────────── time-lapse ───────────────────────── */
const lapse = new Lapse();
async function runLapse() {
    await runSeq('timelapse', async (sig) => {
        lapse.clear();
        el.result.hidden = true;
        const iv = S.tlIv * 1000, max = S.tlMax;
        const t0 = Date.now();
        let next = t0;
        let grabbing = false;
        const grab = async () => {
            if (grabbing || sig.aborted || !open) return;
            grabbing = true;
            try {
                const f = cam.grabFrame();
                if (f) {
                    // keep the long side ≤ 1920 — a 4K frame per 5 s adds up
                    const s = Math.min(1, 1920 / Math.max(f.width, f.height));
                    const date = new Date();
                    const cv = develop(f, f.width, f.height, { streamW: f.width, streamH: f.height, aspect: S.aspect, mirror: S.mirrorSave && mirrorPreview, filter: S.look, date: null });
                    let out = cv;
                    if (s < 1) {
                        out = document.createElement('canvas'); out.width = Math.round(cv.width * s); out.height = Math.round(cv.height * s);
                        out.getContext('2d').drawImage(cv, 0, 0, out.width, out.height);
                    }
                    await lapse.add(out);
                    shutterSound(true);
                    void date;
                }
            } finally { grabbing = false; }
            // a fixed grid (t0 + k·interval), not «interval after the last grab
            // finished»: a 4K frame takes a moment to develop, and that moment
            // would otherwise be added to every interval
            next += iv;
            if (next <= Date.now()) next = Date.now() + iv;
            badge();
        };
        const badge = () => {
            const leftS = Math.max(0, Math.ceil((next - Date.now()) / 1000));
            setBadge([{ text: T('tl_running', { n: lapse.count, s: leftS }), rec: true },
                { text: C.formatDuration((Date.now() - t0) / 1000) }]);
        };
        if (window.__wakeLock) { try { window.__wakeLock.request(); } catch (_) { } }
        await grab();
        const ticker = workerTicker(250, () => {
            if (sig.aborted) return;
            if (Date.now() >= next) grab().then(() => { if (max && lapse.count >= max) cancelSeq(); });
            else badge();
        });
        await new Promise((resolve) => sig.addEventListener('abort', resolve, { once: true }));
        ticker.stop();
        stage('shot');
        feat('shot', { mode: 'timelapse', n: lapse.count, iv: S.tlIv, fps: S.tlFps });
        showLapseResult();
    });
}
function showLapseResult() {
    if (!lapse.count) return;
    el.result.hidden = false;
    el.resBooth.hidden = true; el.resLapse.hidden = false; el.resProg.hidden = true;
    const p = C.timelapsePlan(lapse.count, S.tlIv, S.tlFps);
    el.resultH.textContent = T('res_tl_title', { n: lapse.count });
    el.resSt.textContent = T('tl_plan', { n: lapse.count, dur: C.formatDuration(p.seconds), x: p.speedup });
    setResultImage(lapse.frames[lapse.frames.length - 1]);
    $('ocResMp4').disabled = lapse.count < 2;
    $('ocResGif').disabled = lapse.count < 2;
}
let _exporting = null;
async function exportLapse(kind) {
    if (_exporting || !lapse.count) return;
    const ac = new AbortController();
    _exporting = ac;
    const prog = el.resProg, bar = prog.querySelector('i');
    prog.hidden = false; bar.style.width = '0%';
    el.resSt.textContent = T('exporting', { p: 0 });
    const onProgress = (f) => { const p = Math.round(Math.min(1, f) * 100); bar.style.width = p + '%'; el.resSt.textContent = T('exporting', { p }); };
    const buttons = el.resLapse.querySelectorAll('button');
    buttons.forEach((b) => { b.disabled = true; });
    try {
        const blob = kind === 'mp4' ? await exportMp4(lapse, S.tlFps, onProgress, ac.signal) : await exportGif(lapse, S.tlFps, onProgress, ac.signal);
        const fmt = kind;
        const prev = document.createElement('canvas');
        const bmp = await createImageBitmap(lapse.frames[lapse.frames.length - 1]);
        prev.width = bmp.width; prev.height = bmp.height; prev.getContext('2d').drawImage(bmp, 0, 0);
        if (bmp.close) bmp.close();
        const rec = await addToRoll(blob, fmt, prev, 'timelapse', new Date());
        el.resSt.textContent = T('tl_saved', { size: C.formatBytes(blob.size) });
        feat('timelapse_export', { kind, n: lapse.count, size: blob.size });
        openViewer(rec.id);
    } catch (e) {
        el.resSt.textContent = T('export_failed', { err: (e && e.code === 'mp4_unsupported') ? T('mp4_unsupported') : ((e && e.message) || String(e)) });
        diag({ issue: 'timelapse_export_failed', kind, msg: String((e && e.message) || e).slice(0, 120) });
    } finally {
        _exporting = null;
        prog.hidden = true;
        buttons.forEach((b) => { b.disabled = false; });
    }
}

/* ───────────────────────── the shutter ───────────────────────── */
function shoot(trigger) {
    if (!open) return;
    if (busy()) { cancelSeq(); return; }
    ensureAudio();
    if (S.mode === 'photo') return shootPhoto(trigger);
    if (S.mode === 'burst') return shootBurst(trigger);
    if (S.mode === 'booth') return shootBooth(trigger);
    if (S.mode === 'timelapse') return runLapse();
    if (S.mode === 'night') return shootNight();
}
function ensureAudio() { audio(); }

/* ───────────────────────── hands-free shutters ───────────────────────── */
const smart = new SmartShutter({
    video: el.video,
    cfg: { faceSources: CFG.faceSources || [] },
    onFire: (kind) => {
        if (!open || busy()) return;
        feat('smart_fire', { kind, mode: S.mode });
        if (kind === 'palm' && S.mode === 'photo') shootPhoto('palm');
        else shoot(kind);
    },
    onMeter: (m) => {
        if (m.state === 'loading') { el.smartState.textContent = T('meter_loading'); return; }
        if (m.kind === 'smile') {
            smartBoxes = m.boxes || null; guideDirty();
            el.smartState.textContent = m.faces ? T('meter_smile', { v: Math.round((m.value || 0) * 100), n: m.faces }) : T('meter_no_face');
            if (!busy()) setBadge(m.faces ? [{ text: '😊 ' + Math.round((m.value || 0) * 100) + '%' }] : null);
        } else if (m.kind === 'palm') {
            el.smartState.textContent = m.value ? T('meter_palm_seen') : T('meter_palm_ready');
            if (!busy()) setBadge(m.value ? [{ text: '✋ ' + Math.round((m.progress || 0) * 100) + '%' }] : null);
        } else if (m.kind === 'clap') {
            if (m.state === 'calibrating') el.smartState.textContent = T('meter_calib');
            else el.smartState.textContent = T('meter_clap', { db: Math.round(m.value), thr: Math.round(m.threshold) });
        }
    },
    onError: (kind, e) => {
        el.smartState.textContent = T('smart_failed', { err: (e && e.message) || String(e) });
        setChips('ocSmart', 'off');
        S.smart = 'off';
        showSmartOptions();
        diag({ issue: 'smart_failed', kind, msg: String((e && e.message) || e).slice(0, 120) });
    },
});
async function setSmart(kind) {
    S.smart = kind;
    if (kind !== 'clap') save('smart', kind); else save('smart', 'off');
    setChips('ocSmart', kind);
    showSmartOptions();
    smartBoxes = null; guideDirty();
    if (!busy()) setBadge(null);
    el.smartState.textContent = '';
    el.smartSum.textContent = kind === 'off' ? '' : T('smart_' + kind);
    if (kind === 'off' || !open) { smart.stop(); if (kind !== 'off' && !open) el.smartState.textContent = T('smart_needs_camera'); return; }
    smart.setSmile({ threshold: S.smileThr / 100, all: S.smileAll });
    smart.setClapSensitivity(S.clapSens / 100);
    await smart.start(kind);
    feat('smart', { kind });
}
function showSmartOptions() {
    document.querySelectorAll('[data-smart]').forEach((n) => { n.hidden = n.dataset.smart !== S.smart; });
}

/* ───────────────────────── phone as a remote shutter ───────────────────────── */
let remote = null;
function remoteSend(msg) { if (remote && remote.connected) remote.send(msg); }
function remoteState() {
    if (!remote) return;
    remote.send(['st', { mode: T('mode_' + S.mode), busy: !!seq, n: items.length, ready: open }]);
}
function remoteShot(thumb) {
    if (!remote || !remote.connected || !thumb) return;
    const fr = new FileReader();
    fr.onload = () => { remote.send(['shot', String(fr.result)]); remoteState(); };
    fr.readAsDataURL(thumb);
}
async function startRemote() {
    const box = $('ocRemoteBox'), st = $('ocRemoteSt');
    box.hidden = false;
    st.textContent = T('meter_loading');
    if (!remote) {
        remote = new RemoteHost({
            onShoot: () => { feat('remote', { ev: 'shoot', open: open ? 1 : 0 }); if (open) shoot('remote'); },
            onStatus: (s, extra) => {
                st.textContent = s === 'on' ? T('remote_connected') : s === 'lost' ? T('remote_lost') : s === 'error' ? T('remote_failed', { err: extra || '' }) : T('remote_wait');
                if (s === 'on') { remoteState(); feat('remote', { ev: 'connected' }); }
            },
        });
    }
    try {
        const code = await remote.start();
        const url = location.origin + '/' + (CFG.locale || 'en') + '/online-camera?remote=' + code;
        $('ocRemoteUrl').value = url;
        const qr = $('ocRemoteQr');
        qr.textContent = '';
        if (window.QRCode) { try { new window.QRCode(qr, { text: url, width: 176, colorDark: '#000', colorLight: '#fff' }); } catch (_) { } }
        st.textContent = T('remote_wait');
        remoteState();
        feat('remote', { ev: 'host' });
    } catch (e) {
        st.textContent = T('remote_failed', { err: (e && e.message) || String(e) });
        diag({ issue: 'remote_host_failed', msg: String((e && e.message) || e).slice(0, 120) });
    }
}
function stopRemote() {
    if (remote) remote.stop();
    $('ocRemoteBox').hidden = true;
    $('ocRemoteQr').textContent = '';
}

/** This page opened on the phone: a remote shutter, no camera of its own. */
function bootRemote(code) {
    $('ocMain').hidden = true;
    const v = $('ocRemoteView'); v.hidden = false;
    // The phone came here for one button, and the page intro sits above it.
    // 'nearest' only moves the page when the remote is off screen (CLAUDE.md
    // allows exactly this form).
    requestAnimationFrame(() => { try { v.scrollIntoView({ block: 'nearest' }); } catch (_) { } });
    const stEl = $('ocRmSt'), btn = $('ocRmShutter'), cnt = $('ocRmCount'), last = $('ocRmLast'), mode = $('ocRmMode');
    stEl.textContent = T('rm_connecting');
    let state = { busy: false, ready: false };
    let guest = null;
    const paint = () => {
        const on = !!(guest && guest.conn);
        btn.dataset.state = state.busy ? 'stop' : 'idle';
        btn.disabled = !on;
        btn.setAttribute('aria-label', state.busy ? T('stop') : T('shutter'));
        if (on) stEl.textContent = !state.ready ? T('rm_no_camera') : state.busy ? T('rm_busy') : T('rm_ready');
    };
    guest = new RemoteGuest({
        code,
        onStatus: (s, extra) => {
            if (s === 'on') { feat('remote', { ev: 'joined' }); paint(); return; }
            btn.disabled = true;
            stEl.textContent = s === 'connecting' ? T('rm_connecting') : s === 'not_found' ? T('rm_not_found') : s === 'lost' ? T('rm_lost') : T('remote_failed', { err: extra || '' });
            if (s !== 'connecting') diag({ issue: 'remote_guest_' + s, msg: String(extra || '').slice(0, 100) });
        },
        onMsg: (m) => {
            if (m[0] === 'st' && m[1]) { state = m[1]; mode.textContent = T('rm_mode', { m: state.mode || '', n: state.n || 0 }); paint(); }
            else if (m[0] === 'cd') { if (m[1] > 0) { cnt.hidden = false; cnt.textContent = String(m[1]); } else cnt.hidden = true; }
            else if (m[0] === 'shot' && typeof m[1] === 'string' && m[1].indexOf('data:image/') === 0) {
                last.src = m[1]; last.hidden = false;
                try { if (navigator.vibrate) navigator.vibrate(40); } catch (_) { }
            }
        },
    });
    btn.addEventListener('click', () => {
        if (!guest.shoot()) { paint(); return; }
        try { if (navigator.vibrate) navigator.vibrate(20); } catch (_) { }
        feat('remote', { ev: 'press' });
        if (window.__wakeLock) { try { window.__wakeLock.request(); } catch (_) { } }
    });
    guest.start().catch((e) => { stEl.textContent = T('remote_failed', { err: (e && e.message) || String(e) }); });
    stage('remote_view');
    window.__ocDebug = { remoteGuest: guest };
}

/* ───────────────────────── the roll (gallery) ───────────────────────── */
const thumbUrls = new Map();
let selecting = false;
const selected = new Set();
function thumbUrl(rec) {
    if (thumbUrls.has(rec.id)) return thumbUrls.get(rec.id);
    const b = rec.thumb || rec.blob;
    try { if (window.__skipBlobAutoTrack) window.__skipBlobAutoTrack(b); } catch (_) { }
    const u = URL.createObjectURL(b);
    thumbUrls.set(rec.id, u);
    return u;
}
function renderRoll() {
    const g = el.rollGrid;
    g.textContent = '';
    const list = items.slice().reverse();
    let bytes = 0;
    for (const rec of list) {
        bytes += rec.size || 0;
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'oc-cell' + (selected.has(rec.id) ? ' sel' : '');
        b.dataset.id = String(rec.id);
        b.setAttribute('aria-label', rec.name);
        const img = document.createElement('img');
        img.alt = ''; img.loading = 'lazy'; img.decoding = 'async';
        img.src = thumbUrl(rec);
        b.appendChild(img);
        if (rec.kind === 'booth' || rec.kind === 'timelapse' || rec.kind === 'night' || rec.kind === 'burst') {
            const k = document.createElement('span'); k.className = 'oc-kind';
            k.textContent = rec.kind === 'timelapse' ? (rec.type === 'image/gif' ? 'GIF' : '▶ MP4') : T('kind_' + rec.kind);
            b.appendChild(k);
        }
        if (selecting) { const t = document.createElement('span'); t.className = 'oc-tick'; t.setAttribute('aria-hidden', 'true'); b.appendChild(t); b.setAttribute('aria-pressed', selected.has(rec.id) ? 'true' : 'false'); }
        g.appendChild(b);
    }
    const n = items.length;
    el.rollCount.textContent = n ? T('roll_count', { n, size: C.formatBytes(bytes) }) : '';
    el.rollEmpty.hidden = n > 0;
    el.dlAll.disabled = !n; el.select.disabled = !n; el.delAll.disabled = !n;
    el.select.textContent = selecting ? T('cancel_select') : T('select');
    el.dlSel.hidden = !selecting; el.delSel.hidden = !selecting;
    el.dlSel.textContent = T('dl_selected', { n: selected.size });
    el.dlSel.disabled = !selected.size; el.delSel.disabled = !selected.size;
    remoteState();
    const last = items[items.length - 1];
    if (last) { el.last.disabled = false; el.last.style.backgroundImage = 'url("' + thumbUrl(last) + '")'; }
    else { el.last.disabled = true; el.last.style.backgroundImage = ''; }
}
function recById(id) { return items.find((r) => String(r.id) === String(id)); }
async function removeRec(rec) {
    await roll.remove(rec.id);
    items = items.filter((r) => r !== rec);
    const u = thumbUrls.get(rec.id); if (u) { URL.revokeObjectURL(u); thumbUrls.delete(rec.id); }
    selected.delete(rec.id);
    renderRoll();
}
async function downloadRecs(list, how) {
    if (!list.length) return;
    if (list.length === 1) {
        window.__downloadFile(list[0].blob, list[0].name);
        feat('download', { n: 1, how });
        return;
    }
    if (!window.__batchDeliver) { toast(T('batch_missing'), 'error'); return; }
    const d = new Date();
    try {
        const r = await window.__batchDeliver.deliver(list.map((x) => ({ name: x.name, blob: x.blob })), { zipName: 'photos_' + C.stamp(d) + '.zip', slug: SLUG });
        feat('download', { n: list.length, how, via: r && r.via });
    } catch (e) { toast(T('download_failed'), 'error'); }
}

/* viewer */
let vIndex = -1, vUrl = null;
function openViewer(id) {
    const list = items.slice().reverse();
    vIndex = list.findIndex((r) => String(r.id) === String(id));
    if (vIndex < 0) return;
    showViewer();
    if (!el.viewer.open) { try { el.viewer.showModal(); } catch (_) { el.viewer.setAttribute('open', ''); } }
}
function showViewer() {
    const list = items.slice().reverse();
    const rec = list[vIndex];
    if (!rec) { closeViewer(); return; }
    if (vUrl) URL.revokeObjectURL(vUrl);
    try { if (window.__skipBlobAutoTrack) window.__skipBlobAutoTrack(rec.blob); } catch (_) { }
    vUrl = URL.createObjectURL(rec.blob);
    el.vMedia.textContent = '';
    let m;
    if ((rec.type || '').indexOf('video/') === 0) { m = document.createElement('video'); m.controls = true; m.playsInline = true; m.loop = true; m.src = vUrl; }
    else { m = document.createElement('img'); m.alt = rec.name; m.src = vUrl; }
    el.vMedia.appendChild(m);
    el.vName.textContent = rec.name;
    const d = new Date(rec.ts);
    let when = '';
    try { when = new Intl.DateTimeFormat(CFG.locale || undefined, { dateStyle: 'medium', timeStyle: 'medium' }).format(d); } catch (_) { when = d.toLocaleString(); }
    el.vMeta.textContent = T('v_info', { w: rec.w, h: rec.h, size: C.formatBytes(rec.size), fmt: (rec.name.split('.').pop() || '').toUpperCase() }) + ' · ' + when;
    $('ocVPrev').disabled = vIndex <= 0;
    $('ocVNext').disabled = vIndex >= list.length - 1;
    const share = $('ocVShare');
    let canShare = false;
    try { canShare = !!(navigator.canShare && navigator.canShare({ files: [new File([rec.blob], rec.name, { type: rec.type })] })); } catch (_) { }
    share.hidden = !canShare;
}
function closeViewer() {
    try { el.viewer.close(); } catch (_) { el.viewer.removeAttribute('open'); }
    el.vMedia.textContent = '';
    if (vUrl) { URL.revokeObjectURL(vUrl); vUrl = null; }
}
function viewerRec() { return items.slice().reverse()[vIndex]; }

/* ───────────────────────── full screen ───────────────────────── */
function isFs() { return document.fullscreenElement === el.vfwrap || document.webkitFullscreenElement === el.vfwrap || el.vfwrap.classList.contains('oc-fakefs'); }
async function goFullscreen(on) {
    if (on) {
        if (isFs()) return;
        const ok = await window.__safeFullscreen(el.vfwrap, { onUnsupported: () => { }, onDenied: () => { } });
        if (!ok) el.vfwrap.classList.add('oc-fakefs');
    } else {
        el.vfwrap.classList.remove('oc-fakefs');
        try {
            if (document.fullscreenElement && document.exitFullscreen) await document.exitFullscreen();
            else if (document.webkitFullscreenElement && document.webkitExitFullscreen) document.webkitExitFullscreen();
        } catch (_) { }
    }
    setTimeout(layoutVf, 60);
}
function onFsChange() {
    if (!document.fullscreenElement && !document.webkitFullscreenElement && kioskOn && !el.vfwrap.classList.contains('oc-fakefs')) exitKiosk();
    setTimeout(layoutVf, 60);
}

/* ───────────────────────── controls ───────────────────────── */
function setChips(id, v) {
    const box = $(id); if (!box) return;
    box.querySelectorAll('[data-v]').forEach((b) => {
        const on = String(b.dataset.v) === String(v);
        b.classList.toggle('on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
}
function chips(id, key, after, cast) {
    const box = $(id); if (!box) return;
    setChips(id, S[key]);
    box.addEventListener('click', (e) => {
        const b = e.target.closest('[data-v]'); if (!b || b.disabled) return;
        const v = cast ? cast(b.dataset.v) : b.dataset.v;
        save(key, v); setChips(id, v);
        if (after) after(v);
    });
}
function bindRange(id, key, fmt, after) {
    const r = $(id), out = $(id + 'v');
    r.value = S[key];
    const show = () => { if (out) out.textContent = fmt ? fmt(+r.value) : r.value; };
    show();
    r.addEventListener('input', () => { save(key, +r.value); show(); if (after) after(+r.value); });
}
function bindCheck(id, key, after) {
    const c = $(id);
    c.checked = !!S[key];
    c.addEventListener('change', () => { save(key, c.checked); if (after) after(c.checked); });
}
function setMode(m, quiet) {
    if (busy()) return;
    save('mode', m);
    el.modes.querySelectorAll('.oc-mode').forEach((b) => {
        const on = b.dataset.mode === m;
        b.classList.toggle('on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    const hasSec = m !== 'photo';
    el.modeSec.hidden = !hasSec;
    el.modeSec.querySelectorAll('[data-for]').forEach((n) => { n.hidden = n.dataset.for !== m; });
    el.modeSum.textContent = T('mode_' + m);
    if (hasSec) el.modeSec.open = true;
    setShutter();
    guideDirty();
    updateTlPlan();
    if (!quiet) feat('mode', { m });
}
function updateAspectLabels() {
    const vw = cam.width || 16, vh = cam.height || 9;
    document.querySelectorAll('#ocAspect [data-v]').forEach((b) => {
        const lbl = C.aspectLabel(b.dataset.v, vw, vh);
        if (lbl) b.textContent = lbl;
    });
}
function updateTlPlan() {
    const n = S.tlMax || Math.round(3600 / S.tlIv);
    const p = C.timelapsePlan(n, S.tlIv, S.tlFps);
    $('ocTlPlan').textContent = T(S.tlMax ? 'tl_plan' : 'tl_plan_hour', { n, dur: C.formatDuration(p.seconds), x: p.speedup });
}
function updateBoothUi() {
    $('ocBoothOutWrap').hidden = S.boothLayout !== 'strip';
    guideDirty();
}
function renderLookThumbs() {
    // each look painted on a tiny still of the current viewfinder (or a gradient before the camera)
    const btns = document.querySelectorAll('#ocLooks .oc-look');
    const base = document.createElement('canvas'); base.width = 96; base.height = 72;
    const bx = base.getContext('2d');
    const r = open && cam.width ? C.centerCrop(cam.width, cam.height, 4, 3) : null;
    if (!r || !window.__drawCameraFrame(bx, el.video, r.x, r.y, r.w, r.h, 0, 0, 96, 72)) {
        const g = bx.createLinearGradient(0, 0, 96, 72);
        g.addColorStop(0, '#f59e0b'); g.addColorStop(0.5, '#10b981'); g.addColorStop(1, '#3b82f6');
        bx.fillStyle = g; bx.fillRect(0, 0, 96, 72);
        bx.fillStyle = '#fde68a'; bx.beginPath(); bx.arc(30, 30, 14, 0, 7); bx.fill();
    }
    btns.forEach((b) => {
        const c = b.querySelector('canvas'); if (!c) return;
        const out = develop(base, 96, 72, { streamW: 96, streamH: 72, aspect: 'full', filter: b.dataset.v, date: null, seed: 3 });
        c.getContext('2d').drawImage(out, 0, 0, c.width, c.height);
    });
}

function wire() {
    el.start.addEventListener('click', () => { ensureAudio(); openCamera({ button: el.start }); });
    el.retry.addEventListener('click', () => { openCamera({ button: el.retry }); });
    el.shutter.addEventListener('click', () => shoot('button'));
    el.modes.addEventListener('click', (e) => { const b = e.target.closest('.oc-mode'); if (b && !b.disabled) setMode(b.dataset.mode); });
    el.flip.addEventListener('click', async () => {
        if (busy()) return;
        const list = el._devices || [];
        const cur = cam.settings.deviceId;
        if (cam.facing) {
            save('device', '');
            await openCamera({ facingMode: cam.facing === 'user' ? 'environment' : 'user', button: el.flip });
        } else if (list.length > 1) {
            const i = list.findIndex((d) => d.deviceId === cur);
            const nx = list[(i + 1) % list.length];
            save('device', nx.deviceId);
            await openCamera({ deviceId: nx.deviceId, button: el.flip });
        }
        feat('flip');
    });
    el.last.addEventListener('click', () => { const r = items[items.length - 1]; if (r) openViewer(r.id); });
    el.fs.addEventListener('click', () => goFullscreen(!isFs()));
    document.addEventListener('fullscreenchange', onFsChange);
    document.addEventListener('webkitfullscreenchange', onFsChange);

    el.device.addEventListener('change', () => { save('device', el.device.value); openCamera({ deviceId: el.device.value, button: el.device }); });
    el.res.value = S.res;
    el.res.addEventListener('change', () => { save('res', el.res.value); if (open) openCamera({ deviceId: cam.settings.deviceId, button: el.res }); });
    el.mirror.addEventListener('change', () => {
        mirrorPreview = el.mirror.checked;
        if (cam.facing !== 'environment') save('mirrorFront', mirrorPreview);
        applyMirror(); guideDirty();
    });
    bindCheck('ocMirrorSave', 'mirrorSave');
    bindCheck('ocSensor', 'sensor', () => describeCamera());
    chips('ocFlashMode', 'flashMode');

    chips('ocAspect', 'aspect', () => { guideDirty(); feat('aspect', { v: S.aspect }); });
    chips('ocTimer', 'timer', null, Number);
    chips('ocFormat', 'format', () => { $('ocQualWrap').hidden = S.format === 'png'; });
    if (!webpEncodes()) {
        const b = document.querySelector('#ocFormat [data-v="webp"]');
        if (b) b.disabled = true;
        $('ocWebpNote').hidden = false;
        if (S.format === 'webp') { save('format', 'jpeg'); setChips('ocFormat', 'jpeg'); }
    }
    $('ocQualWrap').hidden = S.format === 'png';
    bindRange('ocQual', 'quality', (v) => v + '%');
    bindCheck('ocSound', 'sound');
    bindCheck('ocBlink', 'blink');
    bindCheck('ocScreenLight', 'screenLight');
    bindCheck('ocDate', 'date');

    $('ocLooks').addEventListener('click', (e) => {
        const b = e.target.closest('.oc-look'); if (!b) return;
        save('look', b.dataset.v); applyLook(); feat('look', { v: S.look });
    });
    $('ocSecLook').addEventListener('toggle', () => { if ($('ocSecLook').open) renderLookThumbs(); });

    bindRange('ocBurstN', 'burstN', (v) => String(v));
    bindRange('ocBurstIv', 'burstIv', (v) => (v / 10).toFixed(1) + ' ' + T('sec_unit'));
    chips('ocBoothLayout', 'boothLayout', updateBoothUi);
    chips('ocBoothOut', 'boothOut', () => guideDirty());
    chips('ocBoothTheme', 'boothTheme');
    bindRange('ocBoothN', 'boothN', (v) => String(v), () => guideDirty());
    const bcd = $('ocBoothCd'); bcd.value = String(S.boothCd); bcd.addEventListener('change', () => save('boothCd', +bcd.value));
    const cap = $('ocBoothCap'); cap.value = S.boothCap; cap.addEventListener('input', () => save('boothCap', cap.value));
    bindCheck('ocBoothDate', 'boothDate');
    bindCheck('ocBoothKeep', 'boothKeep');
    $('ocKioskBtn').addEventListener('click', () => enterKiosk());
    el.kioskGo.addEventListener('click', () => { ensureAudio(); shoot('kiosk'); });
    el.kioskExit.addEventListener('click', () => exitKiosk());
    el.kioskRes.addEventListener('click', () => kioskReady());

    const tliv = $('ocTlIv'); tliv.value = String(S.tlIv); tliv.addEventListener('change', () => { save('tlIv', +tliv.value); updateTlPlan(); });
    const tlfps = $('ocTlFps'); tlfps.value = String(S.tlFps); tlfps.addEventListener('change', () => { save('tlFps', +tlfps.value); updateTlPlan(); });
    const tlmax = $('ocTlMax'); tlmax.value = String(S.tlMax); tlmax.addEventListener('change', () => { save('tlMax', +tlmax.value); updateTlPlan(); });
    bindRange('ocNightN', 'nightN', (v) => String(v));
    bindRange('ocNightBoost', 'nightBoost', (v) => v ? '+' + v : '0');

    chips('ocSmart', 'smart', (v) => setSmart(v));
    bindRange('ocSmileThr', 'smileThr', (v) => v + '%', (v) => smart.setSmile({ threshold: v / 100 }));
    bindCheck('ocSmileAll', 'smileAll', (v) => smart.setSmile({ all: v }));
    bindRange('ocClapSens', 'clapSens', (v) => v + '%', (v) => smart.setClapSensitivity(v / 100));
    $('ocClapRecal').addEventListener('click', () => smart.recalibrate());
    $('ocRemoteBtn').addEventListener('click', () => startRemote());
    $('ocRemoteStop').addEventListener('click', () => { stopRemote(); feat('remote', { ev: 'stop' }); });
    $('ocRemoteCopy').addEventListener('click', async () => {
        const u = $('ocRemoteUrl').value; if (!u) return;
        try { await navigator.clipboard.writeText(u); toast(T('remote_copied'), 'success'); }
        catch (_) { const i = $('ocRemoteUrl'); i.focus(); i.select(); }
    });

    const grid = $('ocGrid'); grid.value = S.grid; grid.addEventListener('change', () => { save('grid', grid.value); guideDirty(); feat('grid', { v: grid.value }); });
    bindCheck('ocLevel', 'level', (on) => { setLevel(on); feat('level', { on: on ? 1 : 0 }); });
    bindCheck('ocHist', 'hist', () => startAnalysis());
    bindCheck('ocZebra', 'zebra', (on) => { $('ocZebraThrWrap').hidden = !on; startAnalysis(); });
    $('ocZebraThrWrap').hidden = !S.zebra;
    bindRange('ocZebraThr', 'zebraThr', (v) => v + '%');
    bindCheck('ocPeak', 'peak', (on) => { $('ocPeakWrap').hidden = !on; startAnalysis(); });
    $('ocPeakWrap').hidden = !S.peak;
    bindRange('ocPeakSens', 'peakSens', (v) => v + '%');
    chips('ocPeakColor', 'peakColor');
    el.manReset.addEventListener('click', () => resetManual());

    // tap to focus / meter where the camera accepts a point of interest
    el.vf.addEventListener('pointerup', (e) => {
        if (!open || e.target.closest('button')) return;
        if (!cam.supportsPointsOfInterest() || !(cam.caps.focusMode || []).length) return;
        const r = el.vf.getBoundingClientRect();
        let nx = (e.clientX - r.left) / r.width, ny = (e.clientY - r.top) / r.height;
        if (nx < 0 || nx > 1 || ny < 0 || ny > 1) return;
        el.focus.style.left = (nx * 100) + '%'; el.focus.style.top = (ny * 100) + '%';
        el.focus.classList.remove('on'); void el.focus.offsetWidth; el.focus.classList.add('on');
        if (mirrorPreview) nx = 1 - nx;
        const set = { pointsOfInterest: [{ x: nx, y: ny }] };
        if ((cam.caps.focusMode || []).indexOf('single-shot') >= 0) set.focusMode = 'single-shot';
        if ((cam.caps.exposureMode || []).indexOf('single-shot') >= 0) set.exposureMode = 'single-shot';
        cam.apply(set).then((ok) => { feat('tap_focus', { ok: ok ? 1 : 0 }); syncManual(); });
    });

    // roll
    el.rollGrid.addEventListener('click', (e) => {
        const b = e.target.closest('.oc-cell'); if (!b) return;
        const rec = recById(b.dataset.id); if (!rec) return;
        if (selecting) { if (selected.has(rec.id)) selected.delete(rec.id); else selected.add(rec.id); renderRoll(); return; }
        openViewer(rec.id);
    });
    el.dlAll.addEventListener('click', () => downloadRecs(items.slice(), 'all'));
    el.select.addEventListener('click', () => { selecting = !selecting; selected.clear(); renderRoll(); });
    el.dlSel.addEventListener('click', () => downloadRecs(items.filter((r) => selected.has(r.id)), 'selected'));
    el.delSel.addEventListener('click', async () => {
        const list = items.filter((r) => selected.has(r.id));
        if (!list.length || !window.confirm(T('confirm_del', { n: list.length }))) return;
        for (const r of list) await removeRec(r);
        selecting = false; renderRoll();
    });
    el.delAll.addEventListener('click', async () => {
        if (!items.length || !window.confirm(T('confirm_del', { n: items.length }))) return;
        await roll.clear();
        for (const u of thumbUrls.values()) URL.revokeObjectURL(u);
        thumbUrls.clear(); items = []; selected.clear(); selecting = false;
        renderRoll();
        feat('roll_clear');
    });

    // viewer
    $('ocVClose').addEventListener('click', closeViewer);
    el.viewer.addEventListener('close', () => { el.vMedia.textContent = ''; if (vUrl) { URL.revokeObjectURL(vUrl); vUrl = null; } });
    el.viewer.addEventListener('click', (e) => { if (e.target === el.viewer) closeViewer(); });
    $('ocVPrev').addEventListener('click', () => { if (vIndex > 0) { vIndex--; showViewer(); } });
    $('ocVNext').addEventListener('click', () => { if (vIndex < items.length - 1) { vIndex++; showViewer(); } });
    $('ocVDl').addEventListener('click', () => { const r = viewerRec(); if (r) { window.__downloadFile(r.blob, r.name); feat('download', { n: 1, how: 'viewer' }); } });
    $('ocVDel').addEventListener('click', async () => {
        const r = viewerRec(); if (!r) return;
        await removeRec(r);
        if (!items.length) { closeViewer(); return; }
        vIndex = Math.min(vIndex, items.length - 1);
        showViewer();
    });
    $('ocVShare').addEventListener('click', async () => {
        const r = viewerRec(); if (!r) return;
        try { await navigator.share({ files: [new File([r.blob], r.name, { type: r.type })] }); feat('share'); } catch (_) { }
    });
    el.viewer.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') { $('ocVPrev').click(); e.preventDefault(); }
        else if (e.key === 'ArrowRight') { $('ocVNext').click(); e.preventDefault(); }
    });

    // result panel
    $('ocResDl').addEventListener('click', () => { if (lastBooth) { window.__downloadFile(lastBooth.blob, lastBooth.name); feat('download', { n: 1, how: 'booth' }); } });
    $('ocResPrint').addEventListener('click', () => {
        if (!lastBooth) return;
        const [wi, hi] = printInches(lastBooth.layout, lastBooth.output);
        printAtSize(lastBooth.blob, wi, hi, lastBooth.name);
        feat('print', { layout: lastBooth.layout });
    });
    $('ocResAgain').addEventListener('click', () => { setMode('booth'); shoot('again'); });
    $('ocResMp4').addEventListener('click', () => exportLapse('mp4'));
    $('ocResGif').addEventListener('click', () => exportLapse('gif'));
    $('ocResFrames').addEventListener('click', async () => {
        if (!lapse.count || !window.__batchDeliver) return;
        const d = new Date(), base = 'TIMELAPSE_' + C.stamp(d);
        const list = lapse.frames.map((b, i) => ({ name: base + '_' + String(i + 1).padStart(4, '0') + '.jpg', blob: b }));
        try { await window.__batchDeliver.deliver(list, { zipName: base + '.zip', slug: SLUG }); feat('timelapse_export', { kind: 'frames', n: list.length }); }
        catch (_) { toast(T('download_failed'), 'error'); }
    });
    $('ocResDiscard').addEventListener('click', () => { lapse.clear(); el.result.hidden = true; });

    // keyboard: Space / Enter / presentation clickers (PageDown) = shutter, Esc = stop
    document.addEventListener('keydown', (e) => {
        if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey) return;
        if (el.viewer.open) return;
        const t = e.target;
        const tag = (t && t.tagName) || '';
        const typing = tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA' || (t && t.isContentEditable);
        if (e.key === 'Escape') { if (busy()) { cancelSeq(); e.preventDefault(); } return; }
        if (typing) return;
        const isShutterKey = e.key === ' ' || e.key === 'Enter' || e.key === 'PageDown' || e.key === 'AudioVolumeUp' || e.key === 'VolumeUp' || e.key === 'Camera';
        if (!isShutterKey || !open) return;
        // a focused control keeps its own Space/Enter (a chip, a summary, a button)
        if ((e.key === ' ' || e.key === 'Enter') && t && t !== document.body && t.closest && t.closest('button,summary,a,[role="tab"]') && t !== el.shutter) return;
        if (e.repeat) { e.preventDefault(); return; }
        e.preventDefault();
        shoot('key');
    });

    el.video.addEventListener('resize', () => { if (open) { layoutVf(); updateAspectLabels(); describeCamera(); } });
    window.addEventListener('resize', layoutVf);
    if (window.ResizeObserver) new ResizeObserver(() => layoutVf()).observe(el.vf);
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible' && open && window.__wakeLock) { try { window.__wakeLock.request(); } catch (_) { } }
    });
    window.addEventListener('pagehide', () => { smart.stop(); if (remote) remote.stop(); });
}

async function boot() {
    const rc = validCode(new URLSearchParams(location.search).get('remote'));
    if (rc) { bootRemote(rc); return; }
    wire();
    setMode(S.mode, true);
    applyLook();
    updateBoothUi();
    updateTlPlan();
    showSmartOptions();
    setChips('ocSmart', S.smart);
    el.smartSum.textContent = S.smart === 'off' ? '' : T('smart_' + S.smart);
    buildManual();
    el.missing.hidden = true;
    if (S.level) setLevel(true);
    if (!cam.isSupported()) showFail(cam.errorMessage({ name: 'NotSupportedError' }));
    await roll.open();
    items = await roll.list();
    for (const r of items) usedNames.add(r.name);
    renderRoll();
    el.rollMem.hidden = roll.durable;
    // A camera the person already allowed opens by itself, like a camera app does.
    try {
        if (navigator.permissions && navigator.permissions.query && cam.isSupported()) {
            const p = await withTimeout(navigator.permissions.query({ name: 'camera' }), 1500);
            if (p && p.state === 'granted' && !open) openCamera({});
        }
    } catch (_) { }
    window.__ocDebug = { S, cam, roll, get items() { return items; }, shoot, setMode, setSmart, lapse, C };
}

boot();
